// Shared types, constants and helpers for the TalentConnect marketplace.
import { supabase } from '../services/supabase';

export const C = {
  navy: '#0A1628', navyHover: '#111E35', gold: '#C9A84C', black: '#0A0A0A',
  white: '#FFFFFF', surface: '#F5F6F8', border: '#D0D5DD', borderLt: '#E8EBF0',
  text: '#0A0A0A', textSec: '#3D4552', muted: '#6B7280', mutedLt: '#9CA3AF',
  success: '#0A7C42', danger: '#C0222A', amber: '#92640A', teal: '#0D6E8A',
};

export const TARGET_ROLES = [
  'CEO', 'COO', 'CIO', 'CFO', 'Portfolio Manager', 'Fund Manager', 'Trader',
  'Quant Researcher', 'Research Analyst', 'Risk Manager', 'Compliance Officer',
  'Business Development', 'Marketing / IR', 'PM Sourcing', 'Operations Manager',
  'Finance Manager', 'Engineer / Developer', 'Data Scientist', 'IT', 'HR',
  'Admin', 'Legal Counsel', 'Other',
];

export const ASSET_CLASSES = [
  'Hedge Fund', 'PE', 'VC', 'Real Estate', 'Credit', 'Infrastructure',
];

export const SENIORITIES: { value: Seniority; label: string; hint: string }[] = [
  { value: 'junior', label: 'Junior', hint: '0–5 years' },
  { value: 'mid', label: 'Mid', hint: '5–10 years' },
  { value: 'senior', label: 'Senior', hint: '10+ years' },
];

export const RELOCATION_COUNTRIES = [
  'US', 'UK', 'Switzerland', 'France', 'Germany',
  'Singapore', 'Hong Kong', 'UAE', 'Other',
];

export const CV_BUCKET = 'candidate-cvs';

export type Seniority = 'junior' | 'mid' | 'senior';

export function seniorityLabel(s: string | null): string {
  return SENIORITIES.find(x => x.value === s)?.label || '-';
}

export function seniorityColor(s: string | null): string {
  return ({ junior: C.teal, mid: C.amber, senior: C.navy } as Record<string, string>)[s || ''] || C.muted;
}

export interface Candidate {
  id: string;
  owner_id: string | null;
  full_name: string | null;
  current_role: string | null;
  current_employer: string | null;
  hide_employer: boolean;
  description: string | null;
  years_experience: number | null;
  seniority: string | null;
  target_roles: string[] | null;
  asset_classes: string[] | null;
  skills: string[] | null;
  relocation_countries: string[] | null;
  cv_url: string | null;
  visible_on_platform: boolean;
  profile_complete: boolean;
  created_at: string;
  updated_at: string;
}

export interface TalentJob {
  id: string;
  posted_by: string | null;
  firm_id: string | null;
  title: string;
  description: string | null;
  target_roles: string[] | null;
  asset_classes: string[] | null;
  seniority: string[] | null;
  location: string | null;
  relocation_offered: boolean;
  status: string; // active, closed, draft
  created_at: string;
  updated_at: string;
}

export interface TalentJobFlag {
  id: string;
  job_id: string;
  candidate_id: string;
  flagged_by: string | null;
  flagged_at: string;
}

export interface TalentConnection {
  id: string;
  job_id: string | null;
  candidate_id: string | null;
  manager_firm_id: string | null;
  requested_by: string | null;
  status: string; // pending, accepted, declined
  manager_feedback: string | null; // contacted, declined, interviewed, hired
  requested_at: string;
  responded_at: string | null;
}

export interface TalentMessage {
  id: string;
  connection_id: string;
  sender_id: string | null;
  content: string;
  read_at: string | null;
  created_at: string;
}

export const FEEDBACK_OPTIONS = ['contacted', 'declined', 'interviewed', 'hired'];

// The candidate profile is considered complete once it has the essentials
// managers need to evaluate it.
export function isCandidateComplete(c: Partial<Candidate>): boolean {
  return !!(c.full_name && c.current_role && c.seniority && (c.target_roles || []).length > 0);
}

// ── Storage ──────────────────────────────────────────────────────────────────
// Upload a CV PDF into the private bucket under the owner's folder and return
// the stored object path. CVs are streamed through short-lived signed URLs.
export async function uploadCV(ownerId: string, file: File): Promise<string> {
  const ext = file.name.split('.').pop() || 'pdf';
  const path = `${ownerId}/cv-${Date.now()}.${ext}`;
  const { error } = await supabase.storage.from(CV_BUCKET).upload(path, file, { upsert: true });
  if (error) throw new Error(error.message);
  return path;
}

export async function signedCVUrl(path: string | null, expiresIn = 3600): Promise<string | null> {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  const { data } = await supabase.storage.from(CV_BUCKET).createSignedUrl(path, expiresIn);
  return data?.signedUrl ?? null;
}

// ── AI ───────────────────────────────────────────────────────────────────────
// Read a File as a base64 string (no data: prefix), suitable for the Anthropic
// document content block.
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(',')[1] || '');
    };
    reader.onerror = () => reject(new Error('Could not read file'));
    reader.readAsDataURL(file);
  });
}

// Strip markdown code fences the model sometimes wraps JSON in, then parse.
function parseJsonLoose<T>(text: string): T {
  let t = (text || '').trim();
  if (t.startsWith('```')) t = t.replace(/^```(?:json)?\s*/i, '').replace(/```\s*$/i, '').trim();
  return JSON.parse(t) as T;
}

export interface ParsedCV {
  description: string;
  current_role: string;
  current_employer: string;
  years_experience: number;
  seniority: Seniority;
  target_roles: string[];
  asset_classes: string[];
  skills: string[];
  relocation_countries: string[];
}

const CV_PARSER_PROMPT =
  `You are a CV parser for an alternative investment recruitment platform. Extract the following from the CV text provided and return ONLY valid JSON with no markdown:
{
  description: string (2-3 sentence professional summary),
  current_role: string,
  current_employer: string,
  years_experience: number,
  seniority: 'junior' | 'mid' | 'senior',
  target_roles: string[] (from this list only: ${TARGET_ROLES.join(', ')}),
  asset_classes: string[] (from: ${ASSET_CLASSES.join(', ')}),
  skills: string[],
  relocation_countries: string[]
}`;

// Send a CV PDF to the altveno-ai edge function and get structured fields back.
export async function parseCVWithAI(file: File): Promise<ParsedCV> {
  const base64 = await fileToBase64(file);
  const { data, error } = await supabase.functions.invoke('altveno-ai', {
    body: {
      systemPrompt: CV_PARSER_PROMPT,
      messages: [{
        role: 'user',
        content: [
          { type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: base64 } },
          { type: 'text', text: 'Parse this CV and return the JSON described in your instructions.' },
        ],
      }],
    },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  const text = data?.content?.[0]?.text || '';
  const parsed = parseJsonLoose<Partial<ParsedCV>>(text);
  // Normalise: keep only recognised enum values, coerce arrays.
  return {
    description: parsed.description || '',
    current_role: parsed.current_role || '',
    current_employer: parsed.current_employer || '',
    years_experience: Number(parsed.years_experience) || 0,
    seniority: (['junior', 'mid', 'senior'].includes(parsed.seniority as string) ? parsed.seniority : 'mid') as Seniority,
    target_roles: (parsed.target_roles || []).filter(r => TARGET_ROLES.includes(r)),
    asset_classes: (parsed.asset_classes || []).filter(a => ASSET_CLASSES.includes(a)),
    skills: (parsed.skills || []).slice(0, 30),
    relocation_countries: (parsed.relocation_countries || []).filter(r => RELOCATION_COUNTRIES.includes(r)),
  };
}

export interface AIMatch {
  candidate_id: string;
  match_score: number;
  reason: string;
}

// Rank candidates against a job spec via the altveno-ai edge function.
export async function matchCandidatesWithAI(
  job: TalentJob,
  candidates: Candidate[],
): Promise<AIMatch[]> {
  const jobDesc = [
    `Title: ${job.title}`,
    job.description ? `Description: ${job.description}` : '',
    (job.target_roles || []).length ? `Target roles: ${(job.target_roles || []).join(', ')}` : '',
    (job.asset_classes || []).length ? `Asset classes: ${(job.asset_classes || []).join(', ')}` : '',
    (job.seniority || []).length ? `Seniority: ${(job.seniority || []).join(', ')}` : '',
    job.location ? `Location: ${job.location}` : '',
  ].filter(Boolean).join('\n');

  const candidateSummaries = candidates.map(c => ({
    candidate_id: c.id,
    current_role: c.current_role,
    seniority: c.seniority,
    years_experience: c.years_experience,
    target_roles: c.target_roles,
    asset_classes: c.asset_classes,
    skills: c.skills,
    relocation_countries: c.relocation_countries,
    summary: c.description,
  }));

  const systemPrompt =
    `You are a recruitment AI for alternative investments. Given this job spec and candidate list, rank the candidates by fit. Return ONLY valid JSON array: [{candidate_id: string, match_score: number (0-100), reason: string (max 15 words)}]. Job: ${jobDesc}. Candidates: ${JSON.stringify(candidateSummaries)}`;

  const { data, error } = await supabase.functions.invoke('altveno-ai', {
    body: {
      systemPrompt,
      messages: [{ role: 'user', content: 'Rank these candidates for the job and return the JSON array.' }],
    },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  const text = data?.content?.[0]?.text || '';
  const matches = parseJsonLoose<AIMatch[]>(text);
  return Array.isArray(matches) ? matches : [];
}
